import CircularProgress from '@mui/material/CircularProgress';
import React, { useState, useEffect, useCallback } from 'react';
import { useUserProfiles } from 'apis/CompanyAPI/users/useUserProfiles';
import { PaginatedResult } from 'types';
import { PaginationOptions } from 'types/api';
import { ICwUserId } from 'types/user';
import Dialog, { Title, Content } from 'ui/views/dialogs/Dialog';
import { addPage } from 'util/paginationUtils';
import useNotify from 'hooks/useNotify';
import UserList from './UserList';

interface UserListDialogProps<T> {
  isOpen: boolean;
  closeDialog: () => void;
  fetchResource: (paginationOptions?: PaginationOptions) => Promise<PaginatedResult<T>>;
  title: string;
  emptyMessage?: string;
}

function UserListDialog<T extends ICwUserId>(props: UserListDialogProps<T>) {
  const { isOpen, closeDialog, fetchResource, title, emptyMessage } = props;

  const [resource, setResource] = useState<PaginatedResult<T> | undefined>(undefined);
  const [allCwUserIds, setAllCwUserIds] = useState<string[]>([]);

  const notify = useNotify();

  const userProfiles = useUserProfiles(allCwUserIds);

  useEffect(() => {
    const loadResource = async () => {
      if (isOpen && !resource) {
        try {
          const fetchedResource = await fetchResource({ limit: 20 });
          const newCwUserIds = fetchedResource.values.map(value => value.cwUserId);
          setAllCwUserIds(newCwUserIds);
          setResource(fetchedResource);
        } catch (e) {
          closeDialog();
          notify('error', 'Could not load resource. Please try again later.');
        }
      }
    };

    if (isOpen) {
      loadResource();
    } else {
      setResource(undefined);
      setAllCwUserIds([]);
    }
  }, [isOpen, resource, fetchResource, closeDialog, notify]);

  const seeMore = useCallback(
    async (paginationOptions: PaginationOptions) => {
      const newResource = await fetchResource(paginationOptions);
      const newCwUserIds = newResource.values.map(value => value.cwUserId);

      // Update the list of all user IDs to include new ones
      setAllCwUserIds(prevIds => {
        const combined = [...prevIds, ...newCwUserIds];
        return Array.from(new Set(combined)); // Remove duplicates
      });

      setResource(prevResource =>
        prevResource ? addPage(prevResource, newResource, (value: T) => value.cwUserId) : newResource,
      );
      return newResource;
    },
    [fetchResource],
  );

  return (
    <Dialog open={isOpen} onClose={closeDialog} maxWidth="xs">
      <Title padding="single" onClose={closeDialog}>
        {title}
      </Title>
      <Content className="u-content-padding-bottom" paddingX="default">
        {resource ? (
          <UserList
            emptyMessage={emptyMessage}
            resource={resource}
            userProfiles={userProfiles}
            seeMore={async opts => {
              await seeMore(opts);
            }}
          />
        ) : (
          <div className="u-flex-center">
            <CircularProgress />
          </div>
        )}
      </Content>
    </Dialog>
  );
}

export default UserListDialog;
